<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import TokenRow from './TokenRow.vue';
import TokenCreatedModal from './TokenCreatedModal.vue';

export default {
  components: {
    SettingsLayout,
    NextButton,
    BaseSettingsHeader,
    TokenRow,
    TokenCreatedModal,
  },
  data() {
    return {
      showDeleteConfirmationPopup: false,
      showTokenModal: false,
      selectedToken: {},
      createdToken: null,
    };
  },
  computed: {
    ...mapGetters({
      tokens: 'kaptionAccessTokens/getTokens',
      uiFlags: 'kaptionAccessTokens/getUIFlags',
    }),
    tableHeaders() {
      return [
        this.$t('KAPTION_ACCESS_TOKENS.LIST.TABLE_HEADER.NAME'),
        this.$t('KAPTION_ACCESS_TOKENS.LIST.TABLE_HEADER.ASSISTANT'),
        this.$t('KAPTION_ACCESS_TOKENS.LIST.TABLE_HEADER.STATUS'),
        this.$t('KAPTION_ACCESS_TOKENS.LIST.TABLE_HEADER.LAST_USED'),
        this.$t('KAPTION_ACCESS_TOKENS.LIST.TABLE_HEADER.ACTIONS'),
      ];
    },
  },
  mounted() {
    this.$store.dispatch('kaptionAccessTokens/get');
    this.checkForNewToken();
  },
  methods: {
    checkForNewToken() {
      // Check if we just created a new token (passed via route query)
      const rawToken = this.$route.query.raw_token;
      if (rawToken) {
        this.createdToken = { raw_token: rawToken };
        this.showTokenModal = true;
        // Clean up the URL
        this.$router.replace({ query: {} });
      }
    },
    navigateToCreate() {
      this.$router.push({
        name: 'settings_kaption_access_tokens_new',
      });
    },
    openDeletePopup(token) {
      this.showDeleteConfirmationPopup = true;
      this.selectedToken = token;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    hideTokenModal() {
      this.showTokenModal = false;
      this.createdToken = null;
    },
    async confirmDeletion() {
      this.closeDeletePopup();
      try {
        await this.$store.dispatch(
          'kaptionAccessTokens/revoke',
          this.selectedToken.id
        );
        useAlert(this.$t('KAPTION_ACCESS_TOKENS.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('KAPTION_ACCESS_TOKENS.DELETE.API.ERROR_MESSAGE'));
      }
    },
  },
};
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.fetchingList"
    :loading-message="$t('KAPTION_ACCESS_TOKENS.LOADING')"
    :no-records-message="$t('KAPTION_ACCESS_TOKENS.LIST.404')"
    :no-records-found="!tokens.length"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KAPTION_ACCESS_TOKENS.HEADER')"
        :description="$t('KAPTION_ACCESS_TOKENS.DESCRIPTION')"
      >
        <template #actions>
          <NextButton
            blue
            icon="i-lucide-circle-plus"
            :label="$t('KAPTION_ACCESS_TOKENS.HEADER_BTN_TXT')"
            @click="navigateToCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 ltr:pr-4 rtl:pl-4 text-left font-semibold text-n-slate-11 last:text-right last:pr-4"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <TokenRow
            v-for="token in tokens"
            :key="token.id"
            :token="token"
            @delete="openDeletePopup"
          />
        </tbody>
      </table>
    </template>

    <TokenCreatedModal
      v-if="showTokenModal"
      :token="createdToken"
      @close="hideTokenModal"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('KAPTION_ACCESS_TOKENS.DELETE.CONFIRM.TITLE')"
      :message="
        $t('KAPTION_ACCESS_TOKENS.DELETE.CONFIRM.MESSAGE', {
          tokenName: selectedToken.name,
        })
      "
      :confirm-text="$t('KAPTION_ACCESS_TOKENS.DELETE.CONFIRM.YES')"
      :reject-text="$t('KAPTION_ACCESS_TOKENS.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
